<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('dailySaleItemReport.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('dailySaleItemReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <ts-card>
          <ts-card-wrapper>
            <ts-loading-banner :loading="loading">
              <form>
                <fieldset>
                  <legend>{{ $t('dailySaleItemReport.criteria') }}</legend>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label class="required">
                        {{ $t('dailySaleItemReport.date') }}
                      </label>
                      <a-range-picker
                        style="width:100%"
                        v-model="model.date_range"
                        valueFormat="DD-MM-YYYY HH:mm"
                        :show-time="{ format: 'HH:mm' }"
                        format="DD-MM-YYYY HH:mm"
                      />
                      <div class="validate" v-if="errors.has('date_range')">
                        {{ errors.first('date_range') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>
                        {{ $t('dailySaleItemReport.category') }}
                      </label>
                      <a-select
                        style="width:100%"
                        mode="multiple"
                        placeholder="All"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.category_id"
                        :options="categories"
                        @change="fetchProduct"
                      ></a-select>
                      <div class="validate" v-if="errors.has('category_id')">
                        {{ errors.first('category_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row form-group">
                    <div class="col-md-12">
                      <label>
                        {{ $t('dailySaleItemReport.product') }}
                      </label>
                      <a-select
                        style="width:100%"
                        mode="multiple"
                        placeholder="All"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.product_id"
                        :options="products"
                      ></a-select>
                      <div class="validate" v-if="errors.has('product_id')">
                        {{ errors.first('product_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-12">
                      <label>{{ $t('stockTransferReport.reportType') }}</label>
                      <div class="form-group">
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="cashier"
                            :value="'cashier'"
                          />
                          <label for="cashier"> By cashier </label>
                        </div>
                        <div class="radio radio-css radio-inline">
                          <input
                            v-model="model.report_type"
                            type="radio"
                            id="sale"
                            :value="'sale'"
                          />
                          <label for="sale"> By sale </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="row form-group"
                    v-if="model.report_type == 'cashier'"
                  >
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.cashier')
                      }}</label>
                      <a-select
                        style="width:100%"
                        mode="multiple"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.cashier_id"
                        :options="cashiers"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('cashier_id')">
                        {{ errors.first('cashier_id') }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="row form-group"
                    v-if="model.report_type == 'sale'"
                  >
                    <div class="col-md-12">
                      <label class="required">{{
                        $t('dailyReport.saleName')
                      }}</label>
                      <a-select
                        style="width:100%"
                        mode="multiple"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        v-model="model.sale_id"
                        :options="sales"
                        placeholder="All"
                      ></a-select>
                      <div class="validate" v-if="errors.has('sale_id')">
                        {{ errors.first('sale_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="form-group">
                      <div class="col-md-12">
                        <a-radio-group v-model="model.print">
                          <a-radio value="a4">A4</a-radio>
                          <a-radio value="receipt">Receipt</a-radio>
                        </a-radio-group>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        outline
                        color="success"
                        @click.prevent="exportExcel"
                        :waiting="exporting"
                        ><i class="fas fa-file-excel"></i>
                        {{ $t('excel') }}</ts-button
                      >
                    </div>
                    <div class="col-md-6 text-right">
                      <ts-button
                        class="btn-block"
                        color="primary"
                        @click.prevent="preview"
                        :waiting="waiting"
                        ><i class="fas fa-file-pdf text-red"></i>
                        {{ $t('preview') }}</ts-button
                      >
                    </div>
                  </div>
                </fieldset>
              </form>
            </ts-loading-banner>
          </ts-card-wrapper>
        </ts-card>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('dailyReport.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'dailyIncome',
  data () {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      src: null,
      errors: new Errors(),
      categories: [],
      cashiers: [],
      sales: [],
      products: [],
      printOptions: ['a4', 'receipt'],
      model: {
        category_id: undefined,
        cashier_id: undefined,
        product_id: undefined,
        print: 'a4',
        report_type: 'cashier',
        sale_id: undefined,
        date_range: [
          moment().startOf('day').format('DD-MM-YYYY HH:mm'),
          moment().endOf('day').format('DD-MM-YYYY HH:mm')
        ]
      }
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    ...mapActions('report/daily', [
      'saleItem',
      'category',
      'productQuery',
      'user',
      'sale'
    ]),
    async fetchData () {
      this.loading = true
      await this.category().then(response => {
        this.categories = response.data.map(el => ({
          value: el.category_id,
          label: el.category_name_en
        }))
      })
      await this.sale().then(response => {
        this.sales = response.data.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en
        }))
      })
      await this.user()
        .then(response => {
          this.cashiers = response.data.map(el => ({
            value: el.user_id,
            label: el.name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchProduct (value) {
      this.model.product_id = undefined
      this.loading = true
      this.productQuery({
        filter: {
          category_id: _.join(value, ',')
        }
      })
        .then(response => {
          this.products = response.data.map(el => ({
            value: el.product_id,
            label: el.product_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview () {
      this.waiting = true
      this.src = ''
      this.errors = new Errors()
      this.saleItem(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/daily/saleItemExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  },
  watch: {
    'model.report_type': function () {
      this.model.cashier_id = undefined
      this.model.sale_id = undefined
    }
  }
}
</script>
